import { getToken, onMessage } from 'firebase/messaging';
import { getMessagingIfSupported } from './firebase';
import { saveFcmToken } from './fcmTokenService';
import { dispatchInAppToast } from '../components/InAppToast';

const VAPID_KEY = 'BNvZkHZmmFK9SxVlAZwc754JDtIYSD3Oxb8V4ezkfB8338GZwFh_S9GYRVwmgC-iQMSIfbTeBtujZYgJScNkf3U';

const isBrowserPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'Notification' in window &&
  'PushManager' in window;

// 모바일 브라우저 — RN 앱이 푸시를 별도 처리하므로 웹 토큰 등록 시 중복 알림이 됨
const isMobileBrowser = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|Windows Phone/i.test(navigator.userAgent);
};

const registerSwOnce = async () => {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (existing) return existing;
    return await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  } catch (e) {
    console.error('[WebPush] SW 등록 실패:', e);
    return null;
  }
};

/**
 * PC 웹 푸시 등록 — 권한 요청 + 토큰 발급 + Firestore 저장
 * @param {string} uid 사용자 mcode
 * @returns {Promise<string|null>} 발급된 토큰 (실패 시 null)
 */
export const registerWebPush = async (uid) => {
  if (!uid) return null;
  if (!isBrowserPushSupported()) return null;
  if (isMobileBrowser()) return null;

  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;

  if (Notification.permission === 'denied') return null;
  if (Notification.permission === 'default') {
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return null;
  }

  const swReg = await registerSwOnce();
  if (!swReg) return null;

  try {
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    if (!token) return null;
    await saveFcmToken(uid, token, 'web');
    return token;
  } catch (e) {
    console.error('[WebPush] 토큰 발급 실패:', e);
    return null;
  }
};

/**
 * 포그라운드(탭 활성 상태) 메시지 수신 시 시스템 알림 직접 표시.
 * 서비스워커는 백그라운드만 처리하므로 포그라운드는 우리가 띄워야 함.
 */
let foregroundUnsub = null;
export const startForegroundListener = async () => {
  if (foregroundUnsub) return foregroundUnsub;
  if (!isBrowserPushSupported()) return () => {};
  if (isMobileBrowser()) return () => {};
  if (Notification.permission !== 'granted') return () => {};

  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};

  foregroundUnsub = onMessage(messaging, (payload) => {
    const data = payload.data || {};
    const noti = payload.notification || {};
    const title = noti.title || data.title || '풀잎하우스';
    const body = noti.body || data.body || '새로운 알림이 도착했습니다.';
    // 탭 활성 상태에서는 카톡 스타일 인앱 토스트로만 표시 (OS 알림 중복 방지)
    dispatchInAppToast({ title, body, type: data.type || '', data });
  });

  return foregroundUnsub;
};

export const stopForegroundListener = () => {
  if (foregroundUnsub) {
    try { foregroundUnsub(); } catch {}
    foregroundUnsub = null;
  }
};
